import React from 'react';
import { PhoneInputProps } from 'react-phone-input-2';
import { StyledPhoneInput, StyledPhoneInputContainer } from './styles';
import { StyledError } from '../textInput/styles';
import { parsePhoneNumberFromString } from 'libphonenumber-js';

interface Props {
  value?: string;
  error?: string;
  label?: string;
  disableErrorMode?: boolean;
  onChange?: (value: string, countryData?: any) => void;
  position?: string;
  required?: boolean;
  countryName?: string;
  bgColor?: string;
  isDisabled?: boolean;
}

const PhoneInputComponent: React.FC<Props & PhoneInputProps> = ({
  error,
  label,
  disableErrorMode,
  onChange,
  position,
  required,
  countryName,
  value,
  bgColor,
  isDisabled,
  ...props
}) => {
  const handleOnChange = (value: string) => {
    if (isDisabled) return;
    if (onChange) {
      onChange(value && !value.startsWith('+') ? `+${value}` : value);
    }
  };

  const defaultCountryCode: string = getUserCountryCode();
  return (
    <StyledPhoneInputContainer isDisabled={isDisabled}>
      <StyledPhoneInput
        value={value}
        preferredCountries={['au', 'gb', 'nz', 'us']}
        specialLabel={label || ''}
        countryCodeEditable={false}
        placeholder={'Mobile'}
        country={defaultCountryCode}
        inputStyle={{
          width: '100%',
          height: '3rem',
          backgroundColor: 'transparent',
        }}
        onChange={handleOnChange}
        bgColor={bgColor}
        isDisabled={isDisabled}
        {...props}
      />
      {!disableErrorMode && error && (
        <StyledError variant='body2'>{error}</StyledError>
      )}
    </StyledPhoneInputContainer>
  );
};

export const fetchCountryCodeFromNumber = (phoneNumber: string): string => {
  if (!phoneNumber) return '';
  const pn = parsePhoneNumberFromString(phoneNumber);
  
  return pn?.countryCallingCode;
};

const getUserCountryCode = (): string => {
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const timeZoneToCountryCode: { [key: string]: string } = {
    // Australia
    'Australia/Sydney': 'au',
    'Australia/Melbourne': 'au',
    'Australia/Brisbane': 'au',
    'Australia/Perth': 'au',
    'Australia/Adelaide': 'au',
    'Australia/Darwin': 'au',
    'Australia/Hobart': 'au',
    'Australia/Broken_Hill': 'au',
    'Australia/Eucla': 'au',
    'Australia/Lord_Howe': 'au',

    // United Kingdom
    'Europe/London': 'gb',

    // New Zealand
    'Pacific/Auckland': 'nz',
    'Pacific/Chatham': 'nz',

    // United States
    'America/New_York': 'us',
    'America/Chicago': 'us',
    'America/Denver': 'us',
    'America/Los_Angeles': 'us',
    'America/Phoenix': 'us',
    'America/Anchorage': 'us',
    'America/Adak': 'us',
    'Pacific/Honolulu': 'us',
    'America/Boise': 'us',
    'America/Detroit': 'us',
    'America/Indiana/Indianapolis': 'us',

    // India
    'Asia/Kolkata': 'in',
    'Asia/Calcutta': 'in',
  };

  return timeZoneToCountryCode[timeZone] ?? 'au';
};

export default PhoneInputComponent;
